import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Heart, ChevronRight, Trophy, Target, TrendingUp, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TeamCard({ teamId, preferredSeason }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/teams/${teamId}`)
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [teamId]);

  if (loading) return <div className="h-44 bg-white/5 rounded-xl animate-pulse" />;
  if (!data) return null;

  const { team, marathon_entries, fixtures, next_fixture } = data;
  // Use preferred season if set, otherwise first entry with played matches
  const marathon = preferredSeason
    ? (marathon_entries?.find(e => e.season === preferredSeason) || marathon_entries?.find(e => e.matches_played > 0) || marathon_entries?.[0])
    : (marathon_entries?.find(e => e.matches_played > 0) || marathon_entries?.[0]);
  const recentMatches = (fixtures || []).filter(f => f.home_goals !== null).slice(0, 3);
  // Use the backend-provided next_fixture, or fallback to finding it in fixtures
  const nextMatch = next_fixture || [...(fixtures || [])]
    .filter(f => f.home_goals === null && f.date)
    .sort((a, b) => new Date(a.date) - new Date(b.date))[0];

  const getDivColor = (div) => {
    const c = {
      "Allsvenskan": "bg-[#FECC00]/20 text-[#FECC00]",
      "Superettan": "bg-slate-600/30 text-slate-300",
    };
    return c[div] || "bg-white/10 text-slate-300";
  };

  return (
    <Link to={`/lag/${teamId}`} className="block group" data-testid={`my-team-${teamId}`}>
      <Card className="border-0 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur hover:from-white/15 transition-all">
        <CardContent className="p-4">
          {/* Header */}
          <div className="flex items-center gap-3 mb-3">
            {team.logo_url ? (
              <img src={team.logo_url} alt="" className="w-10 h-10 object-contain" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-lg font-bold text-white/60">
                {team.name?.charAt(0)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-base font-bold text-white truncate group-hover:text-[#FECC00] transition-colors">{team.name}</p>
              <Badge className={`text-[10px] ${getDivColor(team.current_division)}`}>{team.current_division}</Badge>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-white transition-colors shrink-0" />
          </div>

          {/* Stats row */}
          {marathon && (
            <div className="flex gap-3 mb-3">
              <div className="flex items-center gap-1 text-xs text-slate-400">
                <Trophy className="w-3 h-3 text-[#FECC00]" />
                <span className="font-bold text-white">#{marathon.rank}</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-400">
                <TrendingUp className="w-3 h-3 text-blue-400" />
                <span className="font-bold text-white">{marathon.weighted_points}</span> VP
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-400">
                <Target className="w-3 h-3 text-green-400" />
                <span className="font-bold text-white">{marathon.goals_for}</span> mål
              </div>
            </div>
          )}

          {/* Recent form */}
          {recentMatches.length > 0 && (
            <div className="flex items-center gap-1.5 mb-3">
              <span className="text-[10px] text-slate-500 uppercase mr-1">Form:</span>
              {recentMatches.map((f, i) => {
                const isHome = f.home_team_id === teamId;
                const goalsFor = isHome ? f.home_goals : f.away_goals;
                const goalsAgainst = isHome ? f.away_goals : f.home_goals;
                const r = goalsFor > goalsAgainst ? "V" : goalsFor < goalsAgainst ? "F" : "O";
                const c = r === "V" ? "bg-green-500" : r === "F" ? "bg-red-500" : "bg-slate-500";
                return (
                  <span key={i} className={`w-5 h-5 rounded-full ${c} text-white text-[9px] font-bold flex items-center justify-center`}>
                    {r}
                  </span>
                );
              })}
            </div>
          )}

          {/* Next match */}
          {nextMatch && (
            <div className="pt-2 border-t border-white/10">
              <p className="text-[10px] text-slate-500 uppercase mb-1">Nästa match</p>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-white font-medium truncate">
                  {nextMatch.home_team_id === teamId ? nextMatch.away_team : nextMatch.home_team}
                </span>
                <span className="text-slate-500">
                  ({nextMatch.home_team_id === teamId ? "H" : "B"})
                </span>
                <span className="text-slate-400 ml-auto shrink-0">
                  {nextMatch.date ? new Date(nextMatch.date).toLocaleDateString("sv-SE", { day: "numeric", month: "short" }) : ""}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

export default function MyTeamsSection() {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user?.followed_teams?.length) return null;

  const preferredSeason = user?.preferred_season;

  return (
    <div className="bg-[#0F172A] border-b border-white/5" data-testid="my-teams-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold text-white uppercase tracking-wide flex items-center gap-2">
            <Heart className="w-4 h-4 text-red-400 fill-red-400" /> Mina lag
          </h2>
          {preferredSeason && (
            <Badge className="bg-white/10 text-white/80 text-xs">{preferredSeason}</Badge>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {user.followed_teams.map(tid => (
            <TeamCard key={`${tid}-${preferredSeason}`} teamId={tid} preferredSeason={preferredSeason} />
          ))}
        </div>
      </div>
    </div>
  );
}
